import React from 'react';
import authFetch from '../../../utils/authFetch';

/**
 * Блок для отображения и редактирования данных по пациенту и исследованию.
 * Позволяет вводить ФИО, дату рождения, пол, номер и дату исследования, тип исследования, контраст, примечание и жалобы.
 *
 * Пропсы:
 *  - patientName, setPatientName
 *  - patientBirthdate, setPatientBirthdate
 *  - patientGender, setPatientGender
 *  - studyNumber, setStudyNumber
 *  - studyDate, setStudyDate
 *  - studyType, setStudyType
 *  - contrastType, setContrastType
 *  - studyNote, setStudyNote
 *  - complaints, setComplaints
 *  - templateId - идентификатор текущего шаблона
 *  - alias - полное название исследования для кнопки "Область"
 *  - aiAccess - доступ к AI функциям (boolean)
 */
const PatientInfoBlock = ({
  patientName, setPatientName,
  patientBirthdate, setPatientBirthdate,
  patientGender, setPatientGender,
  studyNumber, setStudyNumber,
  studyDate, setStudyDate,
  studyType, setStudyType,
  contrastType, setContrastType,
  studyNote, setStudyNote,
  complaints, setComplaints,
  templateId,
  alias,
  aiAccess
}) => {
  const [isComplaintsProcessing, setIsComplaintsProcessing] = React.useState(false);
  const complaintsProcessingRef = React.useRef(false);

  // Функция для копирования данных пациента в localStorage
  const copyPatientData = () => {
    const patientData = {
      patientName,
      patientBirthdate,
      patientGender,
      studyNumber,
      studyDate,
      studyType,
      contrastType,
      studyNote,
      complaints
    };
    localStorage.setItem('copied_patient_data', JSON.stringify(patientData));
  };

  // Функция для вставки данных пациента из localStorage
  const pastePatientData = () => {
    const savedData = localStorage.getItem('copied_patient_data');
    if (savedData) {
      try {
        const patientData = JSON.parse(savedData);
        setPatientName(patientData.patientName || '');
        setPatientBirthdate(patientData.patientBirthdate || '');
        setPatientGender(patientData.patientGender || '');
        setStudyNumber(patientData.studyNumber || '');
        setStudyDate(patientData.studyDate || '');
        setStudyType(patientData.studyType || '');
        setContrastType(patientData.contrastType || '');
        setStudyNote(patientData.studyNote || '');
        setComplaints(patientData.complaints || '');
      } catch (error) {
        console.error('Ошибка при вставке данных пациента:', error);
      }
    }
  };

  // Функция для заполнения типа исследования
  const studyTypeAutoFill = async () => {
    console.log('Текущий templateId:', templateId);
    console.log('Alias шаблона:', alias);
    
    if (alias) {
      // Полностью заменяем значение в поле "Исследование" на alias
      setStudyType(alias);
      
      console.log(`Поле "Исследование" заполнено значением "${alias}"`);
    } else {
      alert('Не удалось определить тип исследования для AI генерации');
    }
  };

  // Функция обработки жалоб пациента с помощью AI
  const complaintsGenerationWithAI = async () => {
    if (complaintsProcessingRef.current) {
      return;
    }

    complaintsProcessingRef.current = true;
    setIsComplaintsProcessing(true);

    // Отправляем жалобы пациента на бэкенд и получаем обработанные данные
    try {
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';
      const response = await authFetch(`${API_URL}/process-patient-complaints/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          complaints: complaints
        })
      });

      if (!response.ok) {
        throw new Error(`Backend вернул статус ${response.status}`);
      }

      const result = await response.json();
      console.log('Жалобы пациента успешно обработаны AI:', result);

      if (!result.success || !result.data || !result.data.complaints) {
        throw new Error('Backend не вернул обработанный анамнез');
      }

      const currentComplaints = complaints || '';
      const aiComplaints = result.data.complaints;

      // Если в поле уже есть текст, добавляем пустую строку перед новыми данными
      const separator = currentComplaints.trim() ? '\n\n' : '';
      const newComplaints = currentComplaints.trim() + separator + aiComplaints;

      setComplaints(newComplaints);
      console.log('Данные от AI добавлены в поле жалоб');
    } catch (error) {
      console.error('Ошибка при отправке жалоб пациента на бэкенд:', error);
      alert('Не удалось обработать анамнез. Попробуйте ещё раз.');
    } finally {
      complaintsProcessingRef.current = false;
      setIsComplaintsProcessing(false);
    }
  };

  return (
    <div className="template-block" style={{ paddingLeft: 10, marginTop: 2, marginBottom: 15, background: '#e9ecef' }}>
      <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', marginRight: 0 }}>
        <button
          className="move-arrow-btn"
          disabled
          style={{ marginBottom: 2 }}
          title="Переместить вверх"
        >
          ▲
        </button>
        <button
          className="move-arrow-btn"
          disabled
          title="Переместить вниз"
        >
          ▼
        </button>
      </div>
      <div className="zone-description" style={{ flexBasis: '10%', width: 140, minWidth: 140, maxWidth: 140, fontSize: '0.9rem' }}>
        Данные по пациенту
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: 8 }}>
          <button
            type="button"
            className="main-action-btn"
            style={{ height: 28, fontSize: '0.8rem', padding: '0 8px' }}
            onClick={copyPatientData}
            title="Копировать данные пациента"
          >
            Копировать
          </button>
          <button
            type="button"
            className="main-action-btn word"
            style={{ height: 28, fontSize: '0.8rem', padding: '0 8px' }}
            onClick={pastePatientData}
            title="Вставить данные пациента"
          >
            Вставить
          </button>
          {aiAccess && (
            <button
              type="button"
              className="main-action-btn norma"
              style={{ height: 28, fontSize: '0.8rem', padding: '0 8px' }}
              onClick={studyTypeAutoFill}
              title="Заполнить тип исследования"
            >
              Область
            </button>
          )}
          {aiAccess && (
            <button
              type="button"
              className="main-action-btn norma"
              style={{ height: 28, fontSize: '0.8rem', padding: '0 8px' }}
              onClick={complaintsGenerationWithAI}
              disabled={isComplaintsProcessing}
              title="Заполнить жалобы и анамнез с помощью AI"
            >
              Анамнез 🤖
            </button>
          )}
        </div>
      </div>
      <div className="block-text" style={{ flex: 1 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{ fontWeight: 600, marginRight: 4, minWidth: 120 }}>Фамилия, имя, отчество</div>
          <input
            type="text"
            value={patientName}
            onChange={e => setPatientName(e.target.value)}
            placeholder="Введите ФИО"
            style={{
              width: 320,
              height: 32,
              minHeight: 'unset',
              fontSize: 15,
              borderRadius: 8,
              border: '1px solid #ccd1d9',
              background: patientName === '' ? '#ffeaea' : '#f4f6f8',
              color: '#2c3e50',
              padding: '4px 8px',
              boxSizing: 'border-box'
            }}
          />
          <div style={{ fontWeight: 600, marginLeft: 8, marginRight: 4, minWidth: 100 }}>Дата рождения</div>
          <input
            type="text"
            value={patientBirthdate}
            onChange={e => setPatientBirthdate(e.target.value)}
            placeholder="Введите дату рождения"
            style={{
              width: 140,
              height: 32,
              minHeight: 'unset',
              fontSize: 15,
              borderRadius: 8,
              border: '1px solid #ccd1d9',
              background: patientBirthdate === '' ? '#ffeaea' : '#f4f6f8',
              color: '#2c3e50',
              padding: '4px 8px',
              boxSizing: 'border-box'
            }}
          />
          <div style={{ fontWeight: 600, marginLeft: 27, marginRight: 0, minWidth: 60 }}>Пол</div>
          <select
            value={patientGender}
            onChange={e => setPatientGender(e.target.value)}
            style={{
              width: 120,
              height: 32,
              minHeight: 'unset',
              fontSize: 15,
              borderRadius: 8,
              border: '1px solid #ccd1d9',
              background: patientGender === '' ? '#ffeaea' : '#f4f6f8',
              color: '#2c3e50',
              padding: '4px 8px',
              boxSizing: 'border-box',
              marginLeft: -9
            }}
          >
            <option value="">Выберите</option>
            <option value="мужской">мужской</option>
            <option value="женский">женский</option>
          </select>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
          <div style={{ fontWeight: 600, marginRight: 4, minWidth: 120 }}>Номер исследования</div>
          <input
            type="text"
            value={studyNumber}
            onChange={e => setStudyNumber(e.target.value)}
            placeholder="Введите номер"
            style={{
              width: 140,
              height: 32,
              minHeight: 'unset',
              fontSize: 15,
              borderRadius: 8,
              border: '1px solid #ccd1d9',
              background: studyNumber === '' ? '#ffeaea' : '#f4f6f8',
              color: '#2c3e50',
              padding: '4px 8px',
              boxSizing: 'border-box',
              marginLeft: 20
            }}
          />
          <div style={{ fontWeight: 600, marginLeft: 160, marginRight: 4, minWidth: 120 }}>Дата исследования</div>
          <input
            type="text"
            value={studyDate}
            onChange={e => setStudyDate(e.target.value)}
            placeholder="Введите дату"
            style={{
              width: 140,
              height: 32,
              minHeight: 'unset',
              fontSize: 15,
              borderRadius: 8,
              border: '1px solid #ccd1d9',
              background: studyDate === '' ? '#ffeaea' : '#f4f6f8',
              color: '#2c3e50',
              padding: '4px 8px',
              boxSizing: 'border-box'
            }}
          />
          <button
            type="button"
            className="main-action-btn"
            style={{ height: 31, minWidth: 72, padding: '0 16px', marginLeft: 28 }}
            onClick={() => {
              const yesterday = new Date();
              yesterday.setDate(yesterday.getDate() - 1);
              const dd = String(yesterday.getDate()).padStart(2, '0');
              const mm = String(yesterday.getMonth() + 1).padStart(2, '0');
              const yyyy = yesterday.getFullYear();
              setStudyDate(`${dd}.${mm}.${yyyy}`);
            }}
          >
            Вчера
          </button>
          <button
            type="button"
            className="main-action-btn word"
            style={{ height: 31, minWidth: 72, padding: '0 16px', marginLeft: -10 }}
            onClick={() => {
              const now = new Date();
              const dd = String(now.getDate()).padStart(2, '0');
              const mm = String(now.getMonth() + 1).padStart(2, '0');
              const yyyy = now.getFullYear();
              setStudyDate(`${dd}.${mm}.${yyyy}`);
            }}
          >
            Сегодня
          </button>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 2, marginTop: 8 }}>
          <div style={{ fontWeight: 600, marginRight: 4, minWidth: 100 }}>Исследование</div>
          <input
            type="text"
            value={studyType}
            onChange={e => setStudyType(e.target.value)}
            placeholder="Тип исследования"
            style={{
              width: 520,
              height: 32,
              minHeight: 'unset',
              fontSize: 15,
              borderRadius: 8,
              border: '1px solid #ccd1d9',
              background: studyType === 'Магнитно-резонансная томография' ? '#ffeaea' : '#f4f6f8',
              color: '#2c3e50',
              padding: '4px 8px',
              boxSizing: 'border-box'
            }}
          />
          <select
            value={contrastType}
            onChange={e => setContrastType(e.target.value)}
            style={{
              width: 180,
              height: 32,
              minHeight: 'unset',
              fontSize: 15,
              borderRadius: 8,
              border: '1px solid #ccd1d9',
              background: '#f4f6f8',
              color: '#2c3e50',
              padding: '4px 8px',
              boxSizing: 'border-box',
              marginLeft: 8
            }}
          >
            <option value="">без контрастирования</option>
            <option value="с контрастированием">с контрастированием</option>
          </select>
          <input
            type="text"
            value={studyNote}
            onChange={e => setStudyNote(e.target.value)}
            placeholder="Примечание"
            style={{ width: 180, height: 32, minHeight: 'unset', fontSize: 15, borderRadius: 8, border: '1px solid #ccd1d9', background: '#f4f6f8', color: '#2c3e50', padding: '4px 8px', boxSizing: 'border-box', marginLeft: 8 }}
          />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
          <div style={{ fontWeight: 600, marginRight: 4, minWidth: 320 }}>Жалобы, краткий анамнез заболевания:</div>
          <textarea
            value={complaints}
            onChange={e => setComplaints(e.target.value)}
            placeholder="Жалобы, анамнез..."
            style={{
              width: 680,
              minHeight: 100,
              fontSize: 15,
              borderRadius: 8,
              border: '1px solid #ccd1d9',
              background: complaints === '' ? '#ffeaea' : '#f4f6f8',
              color: '#2c3e50',
              padding: '8px',
              boxSizing: 'border-box',
              resize: 'vertical'
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default PatientInfoBlock;
